import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOMeta from '@/components/base/SEOMeta';
import { categories, allArticles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);

  const filteredArticles = allArticles
    .filter((a) => a.category === slug)
    .sort((a, b) => b.publishDate.localeCompare(a.publishDate));

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400" />
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">分类未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">该分类不存在或已被移除</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${currentCategory.name} - 寸止挑战训练中心 | 极限控制力测试全攻略`}
        description={currentCategory.description}
        keywords={`寸止挑战,${currentCategory.name},寸止挑战技巧,寸止挑战训练方法,极限控制力训练`}
        canonicalUrl={`https://cunzhitiaozhan.com/category/${slug}`}
      />

      <Navbar />

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: currentCategory.name },
            ]}
          />

          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                <i className={`${currentCategory.icon} text-lg`} />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                {currentCategory.name}
              </h1>
            </div>
            <p className="text-foreground-500 text-sm max-w-2xl">{currentCategory.description}</p>
          </div>

          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/70 transition-colors"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
                    <span className="text-xs text-foreground-400">{article.publishDate}</span>
                  </div>
                  <h3 className="font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors text-base mb-2 line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {article.tags.map((tag) => (
                      <span key={tag} className="px-2 py-0.5 rounded-md bg-background-200 text-foreground-500 text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredArticles.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                <i className="ri-file-list-3-line text-2xl text-foreground-400" />
              </div>
              <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}